import * as SliderPrimitive from '@radix-ui/react-slider';
import { forwardRef } from 'react';
import { styled } from '../../stitches.config';

export const StyledSlider = styled(SliderPrimitive.Root, {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  variants: {
    variant: {
      primary: {},
    },
    size: {
      base: {
        height: 8,
        borderRadius: 3,
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'base',
  },
});

export const StyledSliderTrack = styled(SliderPrimitive.Track, {
  position: 'relative',
  flexGrow: 1,
  width: '100%',
  variants: {
    variant: {
      primary: {
        backgroundColor: '$neutral-blue-200',
      },
    },
    size: {
      base: {
        height: 8,
        borderRadius: 3,
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'base',
  },
});

export const StyledSliderThumb = styled(SliderPrimitive.Thumb, {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '$full',
  borderStyle: 'solid',
  variants: {
    variant: {
      primary: {
        backgroundColor: '$primary-base',
        borderColor: 'white',
        focus: {
          outline: 'none',
          borderColor: '$primary-200',
        },
      },
    },
    size: {
      base: {
        width: 20,
        height: 20,
        borderWidth: 3,
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'base',
  },
});

export const StyledSliderThumbTick = styled('div', {
  display: 'block',
  height: '70%',
  variants: {
    variant: {
      primary: {
        backgroundColor: 'white',
      },
    },
    size: {
      base: {
        height: 8,
        borderRadius: 3,
        width: 3,
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'base',
  },
});

export const StyledSliderRange = styled(SliderPrimitive.Range, {
  position: 'absolute',
  height: '100%',
  variants: {
    variant: {
      primary: {
        backgroundColor: '$primary-base',
      },
    },
    size: {
      base: {
        height: 8,
        borderRadius: 3,
      },
    },
  },
  defaultVariants: {
    variant: 'primary',
    size: 'base',
  },
});

export interface SliderProps
  extends Omit<
      React.ComponentProps<typeof StyledSlider>,
      'defaultValue' | 'onAbort' | 'onChange'
    >,
    Omit<SliderPrimitive.SliderProps, 'onChange'> {
  onChange?: SliderPrimitive.SliderProps['onValueChange'];
}

export const Slider = forwardRef<HTMLElement, SliderProps>(
  ({ variant, size, onChange, onValueChange, ...rest }, ref) => (
    <StyledSlider
      onValueChange={(value) => {
        onChange?.(value);
        onValueChange?.(value);
      }}
      variant={variant}
      size={size}
      ref={ref}
      {...rest}
    >
      <StyledSliderTrack variant={variant} size={size}>
        <StyledSliderRange variant={variant} size={size} />
      </StyledSliderTrack>
      <StyledSliderThumb variant={variant} size={size}>
        <StyledSliderThumbTick variant={variant} size={size} />
      </StyledSliderThumb>
    </StyledSlider>
  ),
);

Slider.displayName = 'Slider';
